import React, { useState, useEffect, useCallback } from 'react';
import { storeService } from '../services/StoreService';
import './ParentStoreView.css';
import './StudentDetailPages.css';

function StudentStoreView({ student, schoolId }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showOrders, setShowOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await storeService.getActiveProducts(schoolId);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  const loadOrders = useCallback(async () => {
    if (student?.id) {
      const data = await storeService.getOrdersByStudent(student.id);
      setOrders(data);
    }
  }, [student?.id]);

  useEffect(() => {
    loadProducts();
    loadOrders();
  }, [loadProducts, loadOrders]);

  const categories = ['Uniform', 'Books', 'Crafts', 'Accessories', 'Stationery', 'Other'];

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category === selectedCategory)
    : products;

  const formatCurrency = (amount) => `EC$${(amount || 0).toFixed(2)}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const d = dateValue.toDate ? dateValue.toDate() : new Date(dateValue.seconds ? dateValue.seconds * 1000 : dateValue);
    return isNaN(d) ? 'N/A' : d.toLocaleDateString();
  };

  const getCategoryIcon = (category) => {
    const icons = { 'Uniform': '👕', 'Books': '📚', 'Crafts': '🎨', 'Accessories': '🎒', 'Stationery': '✏️', 'Other': '🛍️' };
    return icons[category] || '🛍️';
  };

  const handlePurchase = async () => {
    if (!selectedProduct) return;
    const total = selectedProduct.price * quantity;
    try {
      await storeService.placeOrder({
        items: [{
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          quantity,
          subtotal: total
        }],
        totalAmount: total,
        paymentMethod,
        studentId: student?.id || null,
        studentName: student?.fullName || null,
        parentId: null,
        parentName: null,
        status: 'Pending',
        schoolId: schoolId || null
      });
      setOrderPlaced(true);
      setOrderMessage(`Your order for ${quantity} x ${selectedProduct.name} has been placed! Total: ${formatCurrency(total)}. The school will contact you about payment and pickup.`);
      loadOrders();
      loadProducts();
    } catch (error) {
      setOrderPlaced(true);
      setOrderMessage('Failed to place order. Please try again.');
    }
  };

  const closeProductDetail = () => {
    setSelectedProduct(null);
    setQuantity(1);
    setPaymentMethod('Cash');
    setOrderPlaced(false);
    setOrderMessage('');
  };

  if (loading) {
    return (
      <div className="store-view">
        <div className="store-loading">Loading store...</div>
      </div>
    );
  }

  if (selectedOrder) {
    const statusKey = (selectedOrder.status || 'Pending').toLowerCase();
    console.log('🔥 StudentStore: Opened order detail', selectedOrder.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedOrder(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">🛍️</div>
          <div>
            <h2 className="detail-page-title">
              Order Details
              <span className={`detail-badge ${statusKey}`}>{selectedOrder.status || 'Pending'}</span>
            </h2>
            <p className="detail-page-subtitle">Placed {formatDate(selectedOrder.createdAt)}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Items</h4>
          <div className="detail-item-list">
            {(selectedOrder.items || []).map((item, idx) => (
              <div key={idx} className="detail-item-row">
                <span>{item.productName} × {item.quantity}</span>
                <span>{formatCurrency(item.subtotal ?? (item.price || 0) * (item.quantity || 0))}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="detail-card">
          <h4>Summary</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">Total</span><span className="detail-value">{formatCurrency(selectedOrder.totalAmount)}</span></div>
            <div className="detail-row"><span className="detail-label">Payment Method</span><span className="detail-value">{selectedOrder.paymentMethod || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value">{selectedOrder.status || 'Pending'}</span></div>
            <div className="detail-row"><span className="detail-label">Order Date</span><span className="detail-value">{formatDate(selectedOrder.createdAt)}</span></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-view">
      <div className="store-header">
        <h2>School Store</h2>
        <button className="orders-btn" onClick={() => setShowOrders(!showOrders)}>
          {showOrders ? 'Back to Store' : 'My Orders'}
        </button>
      </div>

      {showOrders ? (
        <div className="orders-section">
          {orders.length === 0 ? (
            <div className="empty-orders">
              <span className="empty-icon">🛍️</span>
              <p>No orders yet</p>
              <p className="empty-hint">Your purchases will appear here</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map(order => (
                <div key={order.id} className="order-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
                  <div className="order-header">
                    <span className="order-total">{formatCurrency(order.totalAmount)}</span>
                    <span className={`order-status ${(order.status || 'pending').toLowerCase()}`}>
                      {order.status || 'Pending'}
                    </span>
                  </div>
                  {order.items && order.items.map((item, idx) => (
                    <div key={idx} className="order-item">
                      <span>{item.productName}</span>
                      <span>x{item.quantity}</span>
                      <span>{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                  <div className="order-footer">
                    <span>{order.paymentMethod}</span>
                    <span>{new Date(order.createdAt?.seconds ? order.createdAt.seconds * 1000 : order.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="category-filter">
            <button
              className={`category-chip ${!selectedCategory ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                className={`category-chip ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {getCategoryIcon(cat)} {cat}
              </button>
            ))}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="empty-products">
              <span className="empty-icon">📦</span>
              <p>No products available</p>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map(product => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() => { setSelectedProduct(product); setQuantity(1); }}
                >
                  <div className="product-image">
                    {product.imageURL ? (
                      <img src={product.imageURL} alt={product.name} />
                    ) : (
                      <span className="product-placeholder">{getCategoryIcon(product.category)}</span>
                    )}
                  </div>
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <span className="product-price">{formatCurrency(product.price)}</span>
                    <span className={`product-stock ${(product.stockQuantity || 0) <= 0 ? 'out' : 'in'}`}>
                      {(product.stockQuantity || 0) <= 0 ? 'Out of Stock' : `${product.stockQuantity} in stock`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectedProduct && (
        <div className="product-detail-overlay" onClick={closeProductDetail}>
          <div className="product-detail-modal" onClick={(e) => e.stopPropagation()}>
            {orderPlaced ? (
              <div className="order-result">
                <h3>{orderMessage.includes('Failed') ? '❌' : '✅'}</h3>
                <p>{orderMessage}</p>
                <button className="close-btn" onClick={closeProductDetail}>OK</button>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h3>Product Details</h3>
                  <button className="modal-close" onClick={closeProductDetail}>✕</button>
                </div>
                <div className="product-detail-body">
                  <div className="product-detail-image">
                    {selectedProduct.imageURL ? (
                      <img src={selectedProduct.imageURL} alt={selectedProduct.name} />
                    ) : (
                      <span className="product-placeholder-large">{getCategoryIcon(selectedProduct.category)}</span>
                    )}
                  </div>
                  <h2>{selectedProduct.name}</h2>
                  <p className="detail-price">{formatCurrency(selectedProduct.price)}</p>
                  {selectedProduct.description && (
                    <p className="detail-description">{selectedProduct.description}</p>
                  )}
                  <div className="detail-stock">
                    {(selectedProduct.stockQuantity || 0) > 0 ? (
                      <span className="in-stock">✓ {selectedProduct.stockQuantity} in stock</span>
                    ) : (
                      <span className="out-of-stock">✗ Out of stock</span>
                    )}
                  </div>
                  {(selectedProduct.stockQuantity || 0) > 0 && (
                    <>
                      <div className="quantity-selector">
                        <label>Quantity</label>
                        <div className="qty-controls">
                          <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>−</button>
                          <span>{quantity}</span>
                          <button onClick={() => setQuantity(Math.min(selectedProduct.stockQuantity, quantity + 1))} disabled={quantity >= selectedProduct.stockQuantity}>+</button>
                        </div>
                        <span className="total-price">Total: {formatCurrency(selectedProduct.price * quantity)}</span>
                      </div>
                      <div className="payment-method">
                        <label>Payment Method</label>
                        <div className="payment-options">
                          {['Cash', 'Card', 'Online Transfer'].map(method => (
                            <button
                              key={method}
                              className={`payment-chip ${paymentMethod === method ? 'active' : ''}`}
                              onClick={() => setPaymentMethod(method)}
                            >
                              {method}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-actions">
                  {(selectedProduct.stockQuantity || 0) > 0 ? (
                    <button className="purchase-btn" onClick={handlePurchase}>
                      Purchase — {formatCurrency(selectedProduct.price * quantity)}
                    </button>
                  ) : (
                    <button className="purchase-btn disabled" disabled>Out of Stock</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentStoreView;
